import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import BlogTopicForm from './BlogTopicForm';
import { BlogTopicApi } from '../../api/adminApi';
import { primeBlogTopicCache } from '../../hooks/useBlogTopics';
import './BlogTopicsAdmin.css';

const BlogTopicsAdmin = () => {
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const loadTopics = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await BlogTopicApi.getAll();
      setTopics(data);
      primeBlogTopicCache(data);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to load topics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTopics();
  }, []);

  const handleCreate = () => {
    setCurrentTopic(null);
    setIsFormOpen(true);
  };

  const handleEdit = (topic) => {
    setCurrentTopic(topic);
    setIsFormOpen(true);
  };

  const handleDelete = async (topic) => {
    const confirmed = window.confirm(`Delete topic "${topic.name}"?`);
    if (!confirmed) return;
    try {
      await BlogTopicApi.remove(topic.id);
      await loadTopics();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to delete topic');
    }
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setCurrentTopic(null);
  };

  const handleSubmit = async (payload) => {
    setIsSaving(true);
    try {
      if (currentTopic) {
        await BlogTopicApi.update(currentTopic.id, payload);
      } else {
        await BlogTopicApi.create(payload);
      }
      await loadTopics();
      closeForm();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Unable to save topic');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    closeForm();
  };

  return (
    <AdminLayout
      title="Blog Topics"
      description="Organize blogs into reusable topics for filtering and discovery."
      actions={!isFormOpen && (
        <button className="admin-primary-btn" onClick={handleCreate}>
          + Add Topic
        </button>
      )}
    >
      {error && <div className="admin-alert">{error}</div>}

      {isFormOpen ? (
        <BlogTopicForm
          initialData={currentTopic}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSaving={isSaving}
        />
      ) : (
        <div className="services-table-wrapper">
          {loading ? (
            <p>Loading topics…</p>
          ) : (
            <table className="services-table blog-topics-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Slug</th>
                  <th>Sort Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {topics.length === 0 && (
                  <tr>
                    <td colSpan="4">No topics yet.</td>
                  </tr>
                )}
                {topics.map((topic) => (
                  <tr key={topic.id}>
                    <td>{topic.name}</td>
                    <td>{topic.slug}</td>
                    <td>{topic.sortOrder}</td>
                    <td>
                      <div className="table-actions">
                        <button type="button" onClick={() => handleEdit(topic)}>Edit</button>
                        <button type="button" className="danger" onClick={() => handleDelete(topic)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </AdminLayout>
  );
};

export default BlogTopicsAdmin;
